<template>
  <div class="lists-container">
    <div class="lists-header">
      <h2 class="lists-title">
        <i class="fas fa-list-ul me-2"></i>
        Listados
      </h2>
      <p class="lists-subtitle">
        Gestiona tus listas jerárquicas con arrastrar y soltar
      </p>
      <!-- Las ocultas y las vacías no se muestran: una ocultada tiene que salir de en
           medio, y una tarjeta vacía ocupa lugar sin decir nada. El botón es la única
           forma de volver a mostrar una ocultada o de cargarle el primer elemento a una
           vacía, así que no se puede simplemente no dibujarlas. -->
      <button
        v-if="hiddenListsCount"
        type="button"
        class="btn btn-sm btn-outline-secondary lists-hidden-toggle"
        @click="showHidden = !showHidden"
      >
        <i
          :class="showHidden ? 'fas fa-eye-slash' : 'fas fa-eye'"
          class="me-1"
        ></i>
        {{
          showHidden
            ? "Dejar de ver las ocultas y vacías"
            : `Ver listas ocultas y vacías (${hiddenListsCount})`
        }}
      </button>
    </div>

    <div class="lists-grid">
      <div v-for="list in visibleLists" :key="list._id" class="list-card">
        <div class="list-card-header">
          <template v-if="editingListId === list._id">
            <div class="edit-mode">
              <input
                v-model="editedListName[list._id]"
                class="form-control list-name-input"
                @keyup.enter="saveListName(list)"
                @blur="saveListName(list)"
                autofocus
              />
              <div class="edit-buttons">
                <button
                  class="btn btn-success btn-sm"
                  @click.stop="saveListName(list)"
                >
                  <i class="fas fa-check"></i>
                </button>
                <button
                  class="btn btn-secondary btn-sm"
                  @click.stop="cancelEditListName"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="list-header-content">
              <div class="list-info">
                <h3 class="list-name">{{ list.name }}</h3>
                <span class="list-count"
                  >{{ getListCountOptimized(list) }} elementos</span
                >
              </div>
              <div class="list-actions">
                <button
                  v-if="hasPermission('lists.manage')"
                  class="btn btn-outline-primary btn-sm"
                  @click.stop="editListName(list)"
                  title="Editar nombre"
                >
                  <i class="fas fa-edit"></i>
                </button>
                <button
                  v-if="hasPermission('all_access')"
                  class="btn btn-outline-warning btn-sm"
                  @click.stop="clearList(list)"
                  title="Vaciar lista"
                >
                  <i class="fas fa-eraser"></i>
                </button>
                <button
                  v-if="isAdmin"
                  class="btn btn-secondary btn-sm"
                  @click.stop="toggleListVisibility(list)"
                  :title="list.hidden ? 'Mostrar lista' : 'Ocultar lista'"
                >
                  <i :class="list.hidden ? 'fas fa-eye-slash text-danger' : 'fas fa-eye'"></i>
                </button>
                <button
                  v-if="isAdmin"
                  class="btn btn-outline-danger btn-sm"
                  @click.stop="deleteList(list._id)"
                  title="Eliminar lista"
                >
                  <i class="fas fa-trash"></i>
                </button>
                <button
                  class="btn btn-outline-info btn-sm"
                  @click.stop="openListEditor(list)"
                  title="Abrir editor"
                >
                  <i class="fas fa-external-link-alt"></i>
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Modal para editar listado -->
    <div v-if="selectedList" class="modal-overlay" @click="closeListModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fas fa-sitemap me-2"></i>
            {{ selectedList.name }}
            <span v-if="hasUnsavedChanges" class="unsaved-indicator">
              <i class="fas fa-circle me-1"></i>
              Cambios sin guardar
            </span>
          </h3>
          <div class="modal-actions">
            <div class="unsaved-indicator" v-if="hasUnsavedChanges">
              <i class="fas fa-exclamation-triangle text-warning me-1"></i>
              <span class="text-warning">Cambios pendientes</span>
            </div>
            <button
              v-if="hasUnsavedChanges"
              class="btn btn-success btn-sm me-2"
              @click="saveChanges"
              :disabled="isUpdating"
            >
              <i v-if="isUpdating" class="fas fa-spinner fa-spin me-1"></i>
              <i v-else class="fas fa-save me-1"></i>
              {{ isUpdating ? "Guardando..." : "Guardar Cambios" }}
            </button>

            <button class="modal-close" @click="closeListModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="modal-body">
          <div class="tree-header">
            <h4><i class="fas fa-sitemap me-2"></i>Estructura jerárquica</h4>
            <p class="tree-help">
              Arrastra los elementos para crear jerarquías. Usa el icono
              <i class="fas fa-grip-lines"></i> para mover elementos.
            </p>
          </div>
          <div class="tree-content" v-if="selectedList.type">
            <tree-edit
              :nodes="selectedList.values"
              :listId="selectedList.type"
              @update="
                (newTree, operationType) =>
                  onTreeUpdate(selectedList, newTree, operationType)
              "
              @save-requested="saveChanges"
              @backend-delete="onBackendDelete"
            />
          </div>

          <div v-if="canEdit" class="list-actions-panel">
            <div class="add-item-section">
              <h5><i class="fas fa-plus me-2"></i>Agregar elemento</h5>

              <!-- Selector de término padre -->
              <div class="mb-3">
                <label class="form-label">Término padre (opcional)</label>
                <select
                  v-model="selectedParent[selectedList._id]"
                  class="form-select"
                  :disabled="
                    !selectedList.values || selectedList.values.length === 0
                  "
                >
                  <option value="">Sin término padre (elemento raíz)</option>
                  <option
                    v-for="parent in getParentOptions(selectedList)"
                    :key="parent._id || parent.label"
                    :value="parent._id || parent.label"
                  >
                    {{ parent.label }}
                  </option>
                </select>
                <small class="form-text text-muted">
                  Selecciona un término padre para crear un elemento hijo
                </small>
              </div>

              <div class="input-group">
                <input
                  v-model="newValue[selectedList._id]"
                  class="form-control"
                  placeholder="Nombre del nuevo elemento"
                  @keyup.enter="addValue(selectedList)"
                />
                <button class="btn btn-success" @click="addValue(selectedList)">
                  <i class="fas fa-plus me-1"></i>Agregar
                </button>
              </div>
            </div>

            <div class="import-section">
              <h5><i class="fas fa-upload me-2"></i>Importar elementos</h5>
              <textarea
                v-model="selectedList.valuesText"
                class="form-control mb-2"
                placeholder="Un término por línea (usa - para hijos, -- para nietos, etc.):&#10;Wine&#10;-Generic Wines&#10;--Árido de montaña - Generico Blanco&#10;--Árido de montaña - Generico Tinto"
                rows="5"
              ></textarea>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-success"
                  @click="importAllFromText(selectedList)"
                >
                  <i class="fas fa-download me-1"></i>Importar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Crear nuevo listado -->
    <div v-if="hasPermission('all_access')" class="card mt-4">
      <div class="card-body">
        <h5>Nuevo listado</h5>
        <div class="mb-2">
          <label>
            <input type="radio" v-model="importMode" value="text" checked /> Por texto (saltos de línea)
          </label>
          <label class="ms-3">
            <input type="radio" v-model="importMode" value="json" /> JSON
          </label>
        </div>
        <input
          v-model="newList.name"
          class="form-control mb-2"
          placeholder="Nombre"
        />
        <input
          v-model="newList.type"
          class="form-control mb-2"
          placeholder="identificador"
        />
        <textarea
          v-if="importMode === 'text'"
          v-model="newList.valuesText"
          class="form-control mb-2"
          placeholder="Un término por línea (usa - para hijos):&#10;País&#10;-Ciudad&#10;-Región&#10;Otro País&#10;-Su Ciudad"
          rows="4"
        ></textarea>
        <textarea
          v-else
          v-model="newList.valuesJson"
          class="form-control mb-2"
          placeholder='Pega aquí un JSON de items, ej: [{"label":"Argentina","children":[{"label":"Buenos Aires"}]}]'
          rows="4"
        ></textarea>
        <button class="btn btn-primary" @click="createList">Crear</button>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import TreeEdit from "@/components/TreeEdit.vue";
import permissionsMixin from "@/mixins/permissionsMixin";
import Swal from "sweetalert2";

export default {
  components: {
    TreeEdit,
  },
  mixins: [permissionsMixin],
  data() {
    return {
      lists: [],
      importMode: "text",
      newList: { name: "", type: "", valuesText: "", valuesJson: "" },
      newValue: {},
      selectedParent: {}, // Para almacenar el término padre seleccionado para cada lista
      openListId: null,
      editingListId: null,
      editedListName: {},
      role: null,
      selectedList: null,
      hasUnsavedChanges: false,
      isUpdating: false,
      listCountCache: {}, // Cache para conteos de listas
      /** Las listas ocultas se dejan ver con el botón del encabezado. */
      showHidden: false,
    };
  },
  async mounted() {
    this.role = localStorage.getItem("role");
    await this.fetchUserPermissions();
    await this.fetchLists();
  },
  computed: {
    /**
     * Las listas de la grilla. Quedan afuera dos:
     *
     *  - la ocultada a propósito: el backend igual se la manda al admin (para poder
     *    volver a mostrarla), pero dejarla siempre a la vista hacía que ocultarla no
     *    sirviera de nada;
     *  - la que no tiene ningún elemento: una tarjeta vacía ocupa el mismo lugar que una
     *    llena y no dice nada. Desde que cada modelo tiene su propio listado de banderas
     *    son varias, y llenaban la pantalla.
     *
     * Las dos se ven con el botón del encabezado, que es la única forma de volver a
     * mostrar una lista ocultada o de cargarle el primer elemento a una vacía.
     */
    visibleLists() {
      if (this.showHidden) return this.lists;
      return (this.lists || []).filter((list) => !list.hidden && !this.isEmptyList(list));
    },
    hiddenListsCount() {
      return (this.lists || []).filter(
        (list) => list.hidden || this.isEmptyList(list)
      ).length;
    },
    canEdit() {
      return (
        this.hasPermission("lists.view") ||
        this.hasPermission("lists.manage") ||
        this.hasPermission("lists.delete")
      );
    },
    isAdmin() {
      return this.role === "admin";
    },
  },
  methods: {
    /** Sin ningún elemento cargado. */
    isEmptyList(list) {
      return !list || !Array.isArray(list.values) || list.values.length === 0;
    },
    getListCount(list) {
      if (!list || !list.values || !Array.isArray(list.values)) return 0;

      let count = 0;
      const stack = [...list.values]; // Usar stack en lugar de recursión

      while (stack.length > 0) {
        const item = stack.pop();

        // Validar que el item existe
        if (!item || typeof item !== "object") {
          console.warn("Item inválido en getListCount:", item);
          continue;
        }

        count++;

        // Agregar hijos al stack para procesamiento posterior
        if (
          item.children &&
          Array.isArray(item.children) &&
          item.children.length > 0
        ) {
          stack.push(...item.children);
        }
      }

      return count;
    },
    getListCountOptimized(list) {
      // Usar cache para evitar recalcular constantemente
      const cacheKey = list._id;
      if (this.listCountCache[cacheKey] !== undefined) {
        return this.listCountCache[cacheKey];
      }

      const count = this.getListCount(list);
      this.listCountCache[cacheKey] = count;
      return count;
    },
    async fetchLists() {
      const { data } = await wencesApi.get("/api/gotrading/lists");
      // Convierte plano a árbol
      data.forEach((list) => {
        list.values = this.planoATree(list.values);
      });
      this.lists = data;
      // Limpiar cache de conteos
      this.listCountCache = {};
    },
    planoATree(values) {
      if (!values || !Array.isArray(values)) return [];
      const map = {};
      const labelToIdMap = {}; // Mapa para encontrar ID por label
      const roots = [];

      // Primero creamos todos los nodos
      values.forEach((item) => {
        // Validar que el item existe y tiene las propiedades necesarias
        if (!item || typeof item !== "object" || !item.label) {
          console.warn("Item inválido encontrado:", item);
          return; // Saltar elementos inválidos
        }

        // Usar el _id del backend si existe, sino usar el label como identificador temporal
        const nodeId = item._id || item.label;

        map[nodeId] = {
          ...item,
          _id: nodeId,
          children: [],
          expanded: false,
        };
        // Mapear label a ID para búsqueda rápida
        labelToIdMap[item.label] = nodeId;
      });

      // Luego establecemos las relaciones padre-hijo
      values.forEach((item) => {
        // Validar que el item existe y tiene las propiedades necesarias
        if (!item || typeof item !== "object" || !item.label) {
          return; // Saltar elementos inválidos
        }

        // Usar la misma lógica para obtener el nodeId
        const nodeId = item._id || item.label;

        // Verificar que el nodo existe en el mapa
        if (!map[nodeId]) {
          console.warn("Nodo no encontrado en el mapa:", nodeId);
          return;
        }

        if (item.parent) {
          // Buscar el padre por label (ya que el parent puede ser un label o un ID)
          const parentId = labelToIdMap[item.parent];
          if (parentId && map[parentId]) {
            map[parentId].children.push(map[nodeId]);
          } else {
            // Si no encontramos el padre por label, buscar por ID directo
            if (map[item.parent]) {
              map[item.parent].children.push(map[nodeId]);
            } else {
              // Si no encontramos el padre, lo ponemos como raíz
              console.warn(
                `Padre no encontrado para "${item.label}": "${item.parent}"`
              );
              roots.push(map[nodeId]);
            }
          }
        } else {
          roots.push(map[nodeId]);
        }
      });

      return roots;
    },
    treeAPlano(tree, parent = null) {
      if (!tree || !Array.isArray(tree)) return [];

      const plano = [];
      const stack = []; // Usar stack en lugar de recursión
      const MAX_ARRAY_SIZE = 1000000; // Límite seguro para arrays

      // Inicializar stack con todos los nodos raíz
      tree.forEach((node) => {
        if (node && typeof node === "object") {
          stack.push({ node, parent });
        }
      });

      // Procesar iterativamente
      while (stack.length > 0) {
        const { node, parent } = stack.pop();

        // Validar que el nodo existe
        if (!node || typeof node !== "object" || !node.label) {
          console.warn("Nodo inválido en treeAPlano:", node);
          continue;
        }

        const { _id, label, children } = node;

        // Verificar límite del array antes de agregar
        if (plano.length >= MAX_ARRAY_SIZE) {
          console.warn(
            `Array size limit reached (${MAX_ARRAY_SIZE}). Truncating tree conversion.`
          );
          break;
        }

        // Crear objeto con formato correcto para el backend
        const planoItem = {
          label,
        };

        // Solo incluir _id si es un ObjectId válido del backend (no generado en frontend)
        if (_id && _id.length === 24 && /^[0-9a-fA-F]{24}$/.test(_id)) {
          planoItem._id = _id;
        }

        // Asignar parent: ObjectId válido del backend, label del padre, o null
        if (
          parent &&
          parent.length === 24 &&
          /^[0-9a-fA-F]{24}$/.test(parent)
        ) {
          // Es un ObjectId válido del backend
          planoItem.parent = parent;
        } else if (parent && typeof parent === "string") {
          // Es un label del padre (para elementos nuevos)
          planoItem.parent = parent;
        } else {
          // No hay padre
          planoItem.parent = null;
        }

        if (node.labels && typeof node.labels === "object" && !Array.isArray(node.labels)) {
          planoItem.labels = { ...node.labels };
        }

        plano.push(planoItem);

        // Agregar hijos al stack para procesamiento posterior
        if (children && Array.isArray(children) && children.length > 0) {
          children.forEach((child) => {
            if (child && typeof child === "object") {
              // Para elementos con _id del backend, usar el _id como parent
              // Para elementos nuevos, usar el label como parent temporal
              const parentId =
                _id && _id.length === 24 && /^[0-9a-fA-F]{24}$/.test(_id)
                  ? _id
                  : label;
              stack.push({ node: child, parent: parentId });
            }
          });
        }
      }

      return plano;
    },
    async updateList(list, skipRefresh = false) {
      try {
        // Mostrar indicador de carga para listas grandes
        const isLargeList = this.getListCount(list) > 1000;
        if (isLargeList) {
          Swal.fire({
            title: "Guardando...",
            text: "Procesando lista grande, por favor espera...",
            allowOutsideClick: false,
            showConfirmButton: false,
            willOpen: () => {
              Swal.showLoading();
            },
          });
        }

        // Convierte árbol a plano antes de guardar
        const plano = this.treeAPlano(list.values);

        // Verificar si la conversión fue truncada
        if (plano.length >= 1000000) {
          Swal.fire({
            title: "Lista muy grande",
            text: "La lista es demasiado grande para guardar completamente. Se guardará una versión truncada.",
            icon: "warning",
            confirmButtonText: "Continuar",
          });
        }

        await wencesApi.put(`/api/gotrading/lists/${list._id}`, {
          ...list,
          values: plano,
        });

        // Limpiar cache para esta lista específica
        delete this.listCountCache[list._id];

        if (isLargeList) {
          Swal.fire({
            title: "¡Guardado!",
            text: `Se guardaron ${plano.length} elementos correctamente.`,
            icon: "success",
            timer: 2000,
          });
        }

        // Solo refrescar si es necesario (para operaciones como agregar/eliminar)
        if (!skipRefresh) {
          await this.fetchLists();
        }
      } catch (error) {
        console.error("Error saving list:", error);
        Swal.fire({
          title: "Error al guardar",
          text: "Hubo un error al guardar la lista. Por favor, inténtalo de nuevo.",
          icon: "error",
        });
      }
    },
    onTreeUpdate(list, newTree, operationType) {
      // Actualizar inmediatamente en el frontend para fluidez
      list.values = newTree;

      // Solo marcar cambios pendientes para operaciones que requieren guardado manual
      if (operationType === "label-edit" || operationType === "drag-drop") {
        this.hasUnsavedChanges = true;
      }
      // Para 'delete' y 'child-update' no marcar cambios pendientes ya que se manejan automáticamente
    },
    onBackendDelete() {
      // Cuando se elimina del backend, no marcar cambios pendientes
      // porque ya se guardó en el servidor
      this.hasUnsavedChanges = false;
    },
    getParentOptions(list) {
      if (!list || !list.values) return [];

      // Retornar solo elementos que no son hijos (elementos raíz)
      return list.values.filter((item) => !item.isChild);
    },
    async addValue(list) {
      const value = this.newValue[list._id];
      if (!value) return;

      const parentId = this.selectedParent[list._id];
      const isChild = !!parentId;

      try {
        // Llamar al backend para agregar el valor
        const response = await wencesApi.post(
          `/api/gotrading/lists/${list.type}/values`,
          {
            label: value,
            parent: parentId,
            isChild: isChild,
          }
        );

        // Refrescar la lista completa desde el backend para asegurar sincronización
        await this.fetchLists();

        // Actualizar selectedList si está abierto el modal
        if (this.selectedList) {
          const updatedList = this.lists.find(
            (l) => l._id === this.selectedList._id
          );
          if (updatedList) {
            this.selectedList = updatedList;
          }
        }

        // Limpiar formulario
        this.newValue[list._id] = "";
        this.selectedParent[list._id] = "";
        // No marcar cambios pendientes ya que se guardó automáticamente en el backend

        // Mostrar mensaje de éxito
        Swal.fire({
          title: "¡Elemento agregado!",
          text: `Se agregó "${value}" ${
            isChild ? "como elemento hijo" : "como elemento raíz"
          }`,
          icon: "success",
          timer: 2000,
        });
      } catch (error) {
        console.error("Error adding value:", error);
        Swal.fire({
          title: "Error al agregar elemento",
          text: "Hubo un error al agregar el elemento. Por favor, inténtalo de nuevo.",
          icon: "error",
        });
      }
    },
    async createList() {
      // Verificar permisos antes de proceder
      if (!this.hasPermission("all_access")) {
        Swal.fire(
          "Sin permisos",
          "No tienes permisos para crear listados.",
          "error"
        );
        return;
      }

      if (!this.newList.name || !this.newList.type) return;
      let values = [];
      if (this.importMode === "text" && this.newList.valuesText) {
        // Soporte para múltiples niveles con prefijo "-", dividir solo por saltos de línea
        const lines = this.newList.valuesText
          .split(/[\n\r]+/)
          .map((v) => v.trim())
          .filter((v) => v);
        
        const parentStack = []; // Stack para mantener los padres de cada nivel
        
        lines.forEach((line) => {
          // Contar cuántos guiones tiene al inicio para determinar el nivel
          const match = line.match(/^(-+)/);
          const level = match ? match[1].length : 0;
          const label = line.replace(/^-+/, "").trim();
          
          if (!label) return; // Saltar líneas vacías
          
          if (level === 0) {
            // Es un elemento raíz
            values.push({ 
              label: label, 
              isChild: false,
              parent: null
            });
            // Resetear el stack y agregar este elemento
            parentStack.length = 0;
            parentStack.push({ label: label, level: 0 });
          } else {
            // Es un elemento hijo
            // Buscar el padre apropiado (debe estar un nivel arriba)
            let parentLabel = null;
            for (let i = parentStack.length - 1; i >= 0; i--) {
              if (parentStack[i].level === level - 1) {
                parentLabel = parentStack[i].label;
                break;
              }
            }
            // Si no encontramos padre, usar el último del stack o null
            if (!parentLabel && parentStack.length > 0) {
              parentLabel = parentStack[parentStack.length - 1].label;
            }
            
            values.push({
              label: label,
              isChild: level > 0,
              parent: parentLabel
            });
            
            // Actualizar el stack: eliminar elementos del mismo nivel o superiores
            while (parentStack.length > 0 && parentStack[parentStack.length - 1].level >= level) {
              parentStack.pop();
            }
            // Agregar este elemento al stack
            parentStack.push({ label: label, level: level });
          }
        });
      } else if (this.importMode === "json" && this.newList.valuesJson) {
        try {
          // Convierte el árbol a lista plana con isChild, sin _id ni parent
          const parseTree = (arr, isChild = false) => {
            let out = [];
            arr.forEach((item) => {
              out.push({ label: item.label, isChild });
              if (item.children) {
                out = out.concat(parseTree(item.children, true));
              }
            });
            return out;
          };
          const arr = JSON.parse(this.newList.valuesJson);
          values = parseTree(arr, false);
        } catch (e) {
          alert("JSON inválido");
          return;
        }
      }
      await wencesApi.post("/api/gotrading/lists", {
        name: this.newList.name,
        type: this.newList.type,
        values,
      });
      // Un listado creado sin elementos entra vacío y la grilla no lo muestra: se dejan
      // ver las vacías para que no parezca que no se creó.
      if (!values.length) this.showHidden = true;
      this.newList = { name: "", type: "", valuesText: "", valuesJson: "" };
      await this.fetchLists();
    },
    async deleteList(id) {
      console.log("🗑️ [DELETE_LIST] Iniciando eliminación para ID:", id);

      // Verificar permisos antes de proceder
      if (!this.hasPermission("all_access")) {
        console.log("❌ [DELETE_LIST] Sin permisos para eliminar");
        Swal.fire(
          "Sin permisos",
          "No tienes permisos para eliminar listados.",
          "error"
        );
        return;
      }

      console.log(
        "✅ [DELETE_LIST] Permisos verificados, mostrando popup de confirmación"
      );

      const result = await Swal.fire({
        title: "¿Estás seguro?",
        html: `
          <div class="text-start">
            <p>Esta acción eliminará permanentemente este listado.</p>
            <p class="text-danger"><strong>Advertencia:</strong> Todos los contenidos que estén utilizando elementos de este listado se verán afectados y podrían quedar corruptos.</p>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        allowOutsideClick: false,
        allowEscapeKey: false,
        focusConfirm: false,
      });

      console.log("🔍 [DELETE_LIST] Resultado del popup:", result);
      console.log("🔍 [DELETE_LIST] result.isConfirmed:", result.isConfirmed);
      console.log("🔍 [DELETE_LIST] result.value:", result.value);
      console.log("🔍 [DELETE_LIST] result.dismiss:", result.dismiss);

      if (result.isConfirmed) {
        console.log(
          "✅ [DELETE_LIST] Usuario confirmó eliminación, llamando API"
        );
        try {
          console.log(
            "🌐 [DELETE_LIST] Haciendo llamada DELETE a:",
            `/api/gotrading/lists/${id}`
          );
          await wencesApi.delete(`/api/gotrading/lists/${id}`);
          console.log(
            "✅ [DELETE_LIST] API respondió correctamente, refrescando lista"
          );
          await this.fetchLists();
          Swal.fire(
            "¡Eliminado!",
            "El listado ha sido eliminado correctamente.",
            "success"
          );
        } catch (error) {
          console.error(
            "❌ [DELETE_LIST] Error al eliminar el listado:",
            error
          );
          Swal.fire("Error", "Hubo un error al eliminar el listado.", "error");
        }
      } else {
        console.log(
          "❌ [DELETE_LIST] Usuario canceló la eliminación o no se confirmó"
        );
        console.log(
          "❌ [DELETE_LIST] Motivo:",
          result.dismiss || "No se especificó"
        );
      }
    },
    editListName(list) {
      this.editingListId = list._id;
      this.editedListName = { ...this.editedListName, [list._id]: list.name };
    },
    async saveListName(list) {
      if (
        !this.editedListName[list._id] ||
        this.editedListName[list._id] === list.name
      ) {
        this.editingListId = null;
        return;
      }
      list.name = this.editedListName[list._id];
      await this.updateList(list);
      this.editingListId = null;
      await this.fetchLists();
    },
    cancelEditListName() {
      this.editingListId = null;
    },
    openListEditor(list) {
      // Navegar a la página de edición
      this.$router.push(`/lists/${list._id || list.type}/edit`);
    },
    openListModal(list) {
      this.selectedList = list;
    },
    closeListModal() {
      this.selectedList = null;
      this.hasUnsavedChanges = false;
    },
    async saveChanges() {
      if (!this.selectedList || this.isUpdating) return;

      // Verificar si la lista es demasiado grande
      const itemCount = this.getListCount(this.selectedList);
      if (itemCount > 50000) {
        const result = await Swal.fire({
          title: "Lista muy grande",
          html: `
            <div class="text-start">
              <p>Esta lista tiene <strong>${itemCount.toLocaleString()}</strong> elementos.</p>
              <p>Guardar una lista tan grande puede tomar mucho tiempo y causar problemas de rendimiento.</p>
              <p class="text-warning"><strong>Recomendación:</strong> Considera dividir esta lista en listas más pequeñas.</p>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Guardar de todos modos",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#d33",
        });

        if (!result.isConfirmed) {
          return;
        }
      }

      this.isUpdating = true;
      try {
        await this.updateList(this.selectedList, true);
        this.hasUnsavedChanges = false;
      } catch (error) {
        console.error("Error saving changes:", error);
        Swal.fire({
          title: "Error al guardar",
          text: "Hubo un error al guardar los cambios. Por favor, inténtalo de nuevo.",
          icon: "error",
        });
      } finally {
        this.isUpdating = false;
      }
    },
    async importAllFromText(list) {
      if (!list.valuesText) return;

      try {
        // Mostrar indicador de carga
        Swal.fire({
          title: "Procesando...",
          text: "El backend está procesando la lista, por favor espera...",
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          },
        });

        // Enviar el texto tal cual al backend para que lo procese
        const response = await wencesApi.post(
          `/api/gotrading/lists/${list._id}/import-text`,
          {
            text: list.valuesText,
          }
        );

        // Limpiar el textarea
        list.valuesText = "";
        this.hasUnsavedChanges = false;

        // Refrescar la lista
        await this.fetchLists();

        Swal.fire({
          title: "¡Importación completada!",
          text: `Se importaron los elementos correctamente desde el backend.`,
          icon: "success",
          timer: 2000,
        });
      } catch (error) {
        console.error("Error during backend import:", error);
        Swal.fire({
          title: "Error en la importación",
          text: "Hubo un error al procesar la lista en el backend. Por favor, verifica el formato e inténtalo de nuevo.",
          icon: "error",
        });
      }
    },
    async clearList(list) {
      // Verificar permisos antes de proceder
      if (!this.hasPermission("all_access")) {
        Swal.fire(
          "Sin permisos",
          "No tienes permisos para vaciar listados.",
          "error"
        );
        return;
      }

      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: `¿Estás seguro que deseas vaciar el listado "${list.name}"? Esta acción no se puede deshacer.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, vaciar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        try {
          await wencesApi.put(`/api/gotrading/lists/${list._id}`, {
            ...list,
            values: [],
          });
          // Al quedar vacío sale de la grilla: se dejan ver las vacías para que no
          // parezca que se borró el listado entero.
          this.showHidden = true;
          await this.fetchLists();
          Swal.fire(
            "¡Listado vaciado!",
            "El listado ha sido vaciado correctamente.",
            "success"
          );
        } catch (error) {
          console.error("Error al vaciar el listado:", error);
          Swal.fire("Error", "Hubo un error al vaciar el listado.", "error");
        }
      }
    },
    async toggleListVisibility(list) {
      // Verificar que solo admin puede cambiar visibilidad
      if (!this.isAdmin) {
        Swal.fire(
          "Sin permisos",
          "Solo los administradores pueden cambiar la visibilidad de listados.",
          "error"
        );
        return;
      }

      try {
        const response = await wencesApi.put(
          `/api/gotrading/lists/${list._id}/toggle-visibility`
        );
        
        // Actualizar el estado local de la lista
        list.hidden = response.data.hidden;
        
        // Mostrar mensaje de confirmación
        Swal.fire({
          title: list.hidden ? "Lista ocultada" : "Lista visible",
          text: list.hidden
            ? "La lista ahora está oculta para otros usuarios."
            : "La lista ahora es visible para todos los usuarios.",
          icon: "success",
          timer: 2000,
        });
        
        // Refrescar la lista para asegurar sincronización
        await this.fetchLists();
      } catch (error) {
        console.error("Error al cambiar visibilidad:", error);
        Swal.fire({
          title: "Error",
          text: "Hubo un error al cambiar la visibilidad de la lista.",
          icon: "error",
        });
      }
    },
  },
};
</script>

<style scoped>
.lists-container {
  padding: 20px;
}

.lists-header {
  text-align: center;
  margin-bottom: 40px;
}

/* Debajo del subtítulo, centrado como el resto del encabezado. */
.lists-hidden-toggle {
  margin-top: 4px;
}

.lists-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 10px;
}

.lists-subtitle {
  font-size: 1.1rem;
  color: #7f8c8d;
  margin: 0;
}

.lists-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.list-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  overflow: hidden;
  transition: all 0.3s ease;
}

.list-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.list-card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.list-card-header:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
}

.edit-mode {
  display: flex;
  align-items: center;
  gap: 12px;
}

.list-name-input {
  flex: 1;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 1.1rem;
  font-weight: 600;
}

.edit-buttons {
  display: flex;
  gap: 8px;
}

.list-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.list-info {
  flex: 1;
}

.list-name {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: white;
}

.list-type {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  margin-right: 12px;
}

.list-count {
  background: rgba(255, 255, 255, 0.15);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
}

.list-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.list-actions .btn {
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.list-actions .btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.expand-icon {
  margin-left: 12px;
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.list-card-content {
  padding: 24px;
  background: #f8f9fa;
}

.tree-container {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid #e9ecef;
}

.tree-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f1f3f4;
}

.tree-header h4 {
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.tree-help {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
}

.tree-content {
  min-height: 100px;
  max-height: 400px;
  overflow-y: auto;
  padding: 12px;
  background: #fafbfc;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.list-actions-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.add-item-section,
.import-section {
  background: white;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.add-item-section h5,
.import-section h5 {
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 16px 0;
}

.input-group {
  display: flex;
  gap: 8px;
}

.input-group .form-control {
  flex: 1;
  border-radius: 8px;
  border: 1px solid #ced4da;
  padding: 10px 12px;
  transition: border-color 0.3s ease;
}

.input-group .form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.btn {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: none;
  padding: 10px 16px;
}

.btn-success {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
}

.btn-success:hover {
  background: linear-gradient(135deg, #218838, #1ea085);
  transform: translateY(-1px);
}

.btn-primary {
  background: linear-gradient(135deg, #007bff, #6610f2);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #0056b3, #520dc2);
  transform: translateY(-1px);
}

.btn-outline-primary {
  border: 1px solid #007bff;
  color: #007bff;
  background: transparent;
}

.btn-outline-primary:hover {
  background: #007bff;
  color: white;
}

.btn-outline-warning {
  border: 1px solid #ffc107;
  color: #ffc107;
  background: transparent;
}

.btn-outline-warning:hover {
  background: #ffc107;
  color: #212529;
}

.btn-outline-danger {
  border: 1px solid #dc3545;
  color: #dc3545;
  background: transparent;
}

.btn-outline-danger:hover {
  background: #dc3545;
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

/* Responsive design */
@media (max-width: 768px) {
  .lists-grid {
    grid-template-columns: 1fr;
  }

  .list-actions-panel {
    grid-template-columns: 1fr;
  }

  .list-header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .list-actions {
    width: 100%;
    justify-content: space-between;
  }
}

/* Scrollbar styling */
.tree-content::-webkit-scrollbar {
  width: 6px;
}

.tree-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.tree-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.tree-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s ease-out;
}

.modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 90vw;
  max-height: 90vh;
  width: 1000px;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 16px 16px 0 0;
  gap: 16px;
}

.modal-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  flex: 1;
}

.unsaved-indicator {
  font-size: 0.9rem;
  font-weight: 400;
  opacity: 0.9;
  margin-left: 16px;
  display: flex;
  align-items: center;
  color: #ffeb3b;
}

.modal-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-actions .btn {
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.modal-actions .btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.modal-actions .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.2rem;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.modal-body {
  padding: 32px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.modal-body .tree-container {
  flex: 1;
  min-height: 400px;
}

.modal-body .tree-content {
  max-height: 500px;
  min-height: 300px;
}

.modal-body .list-actions-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 24px;
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Responsive para modal */
@media (max-width: 768px) {
  .modal-content {
    width: 95vw;
    max-height: 95vh;
  }

  .modal-header {
    padding: 20px 24px;
  }

  .modal-body {
    padding: 24px;
  }

  .modal-body .list-actions-panel {
    grid-template-columns: 1fr;
  }

  .modal-title {
    font-size: 1.3rem;
  }
}
</style>
